import React, { useState, useEffect } from 'react';
import styled, { StylesProps } from '../../../../../lib/styles';
import Spacer from '../../../../../components/presentational/Spacer';
import Button from '../../../../../components/presentational/Button';
import Icon from '../../../../../components/presentational/Icon';

import membershipBlockStyles from './MembershipBlock_styles';
import Typography from '../../../../../components/presentational/Typography';
import { Member, MemberLevelSubscription } from '../../../../../models/user';
import {
  getMemberInformation, signOut,
} from '../../../../../lib/accountUtils';
import { TrackingProfile, getProfileTracker, mapMemberLevelSubscriptions } from '../../../../../lib/accountUtils';
import { useMountEffect } from '../../../../../lib/hooks';
import { months, constructNumberRange } from '../../../../../lib/dateUtilities';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import MembershipSignupPanel from '../../../../../components/presentational/MemberCentre/MembershipSignupPanel';
import MembershipPanel from '../../../../../components/presentational/MemberCentre/MembershipPanel';
import { saveMemberProfile } from '../../../../../lib/api/profile';
import Spinner from '../../../../../components/presentational/Spinner';
import { compose } from 'recompose';
import dayjs from 'dayjs';
import clsx from 'clsx';
import _ from 'lodash';
import { Pixel } from '../../../../../components/presentational/Pixel';
import { MEMBERCENTRE_UNSUBSCRIBE_CLICK, MEMBERCENTRE_UPDATE_CLICK, raiseEvent } from '../../../../../lib/gtm';
dayjs.extend(customParseFormat);

export interface MembershipBlockData {
  type: 'membership_block';
  joinBackgroundImage: string;
  joinPersonImage: string;
  joinTitle: string;
  joinDescription: string;
  joinEmailTextboxPlaceholder: string;
  joinButtonText: string;
  joinConfirmationText: string;

  detailsMaleAvatarImage: string;
  detailsFemaleAvatarImage: string;
  detailsUndefinedGenderAvatarImage: string;
  detailsWelcomeText: string;
  detailsTitle: string;
  detailsManageSubscriptionsButtonText: string;
  detailsManageProfileButtonText: string;
  detailsUnsubscribedMemberWelcomeText: string;

  profileTitle: string;
  profileDescription: string;

  subscriptionsTitle: string;
  subscriptionsDescription: string;

  saveButtonText: string;
  saveSuccessConfirmationText: string;
  unsubscribeButtonText: string;
  resubscribeButtonText: string;
  unsubscribePromptText: string;
  unsubscribeConfirmationText: string;

  impersonating: string;
  cloak: boolean;
}

export interface MembershipBlockProps {
  block: MembershipBlockData;
  blockIndex: number;
}

export const MembershipBlock = ({
  block,
  styles,
}: MembershipBlockProps & StylesProps<typeof membershipBlockStyles>) => {
  const [isMember, setIsMember] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userInformation, setUserInformation] = useState<Member>({
    subscriptions: [],
    dotDigital: {},
  });
  const [impersonation, setImpersonation] = useState<{email: string, cloak: boolean}>(
    {
      email: block.impersonating, 
      cloak: block.cloak
    }
  );
  const [trackerData, setTrackerData] = useState<TrackingProfile | undefined>(undefined);

  const [isSaving, setIsSaving] = useState(false);
  const [memberEmail, setMemberEmail] = useState<any>('');
  const [firstName, setFirstName] = useState<any>('');
  const [lastName, setLastName] = useState<any>('');
  const [dob, setDOB] = useState<any>('');
  const [dobDay, setDOBDay] = useState<string>('14');
  const [dobMonth, setDOBMonth] = useState<string>('');
  const [dobYear, setDOBYear] = useState<string>('1966');
  const [postcode, setPostcode] = useState<any>('');
  const [gender, setGender] = useState<any>('');
  const [allSubscriptions, setAllSubscriptions] = useState<
    MemberLevelSubscription[]
  >([]);
  const [unsubscribeDate, setUnsubscribeDate] = useState<any>('');
  const [dotDigital, setDotDigital] = useState<any>('');

  const [subscriptionCheckedState, setSubscriptionCheckedState] = useState<
    boolean[]
  >(new Array(allSubscriptions.length).fill(false));
  const [submitSuccessful, setSubmitSuccessful] =
    useState<boolean | undefined>(undefined);
  const [submitMessage, setSubmitMessage] = useState('');
  const [promptUnsubscribe, setPromptUnsubscribe] = useState(false);
  const [promptUnsubscribeSuccessful, setPromptUnsubscribeSuccessful] =
    useState<boolean | undefined>(undefined);
  const [promptConfirmEmail, setPromptConfirmEmail] = useState('');
  const [signOutIsComplete, setsignOutIsComplete] = useState(false);

  useMountEffect(() => {
    //  otherwise we need to get the email address from the profile tracker
    getProfileTracker().then(trackerData => {
      setTrackerData(trackerData);
      setImpersonation(prevState => {
        return {
          ...prevState,
          ...{
            email: impersonation.email || trackerData.email || '',
            cloak: impersonation.cloak
          }
        }
      });
    });
  });

  useEffect(() => {
    //  this is bound to changes of the impersonation object
    getMemberInformation(impersonation.email)
      .then(profileData => {
        setUserInformation(prevState => {
          return {
            ...prevState,
            ...profileData,
          };
        });
        return profileData;
      })
      .finally(() => {
        //  make sure all checks have been completed before setting isLoading to false
        if (isMember || (_.isEmpty(trackerData))) {
          //  tracker checked but nothing returned - all processes are complete
          setIsLoading(false);
        }
      })
      .catch(() => {
        setIsMember(false);
      });
  }, [impersonation, trackerData]);

  //  reset success states when fields are altered
  useEffect(() => {
    setSubmitSuccessful(undefined);
    setPromptUnsubscribeSuccessful(undefined);
  }, [firstName, lastName, dobDay, dobMonth, dobYear, postcode, gender]);

  useEffect(() => {
    setIsMember(userInformation.id || userInformation.dotDigital.email);
    setMemberEmail(userInformation.email);
    setFirstName(userInformation.given_names);
    setLastName(userInformation.last_name);
    setDotDigital(userInformation.dotDigital);
    setDOB(dayjs(userInformation.dob));

    setDOBDay(
      userInformation.dob ? dayjs(userInformation.dob).format('D') : dobDay
    );
    setDOBMonth(
      userInformation.dob ? dayjs(userInformation.dob).format('M') : dobMonth
    );
    setDOBYear(
      userInformation.dob ? dayjs(userInformation.dob).format('YYYY') : dobYear
    );
    setPostcode(userInformation.postcode);
    setGender(userInformation.gender || '');
    setUnsubscribeDate(userInformation.unsubscribe_date);

    refreshMemberSubscriptions();
  }, [userInformation]);

  const refreshMemberSubscriptions = () => {
    //  merge existing subscriptions into all subscriptions

    mapMemberLevelSubscriptions(userInformation.subscriptions).then(
      globalSubscriptions => {
        //  cache that
        setAllSubscriptions(globalSubscriptions);
        setSubscriptionCheckedState(
          globalSubscriptions.map(sub => sub.isSelected ?? false)
        );
      }
    );
  };

  const calculateUnsubscribeDate = () => {
    //  if unsubscribing and not already unsubscribed, set unsubscribe date to now
    //  else if unsubscribing and already unsubscribed, keep the current unsubscribe date
    //  else set unsubscribe date to null
    return promptUnsubscribe && !userInformation.unsubscribe_date
      ? dayjs().format('YYYY-MM-DD')
      : promptUnsubscribe && unsubscribeDate
      ? dayjs(unsubscribeDate).format('YYYY-MM-DD')
      : null;
  };

  const validate = () => {
    //  validate fields
    if (
      !memberEmail ||
      !firstName ||
      !lastName ||
      !postcode ||
      !dob ||
      !gender
    ) {
      return { isValid: false, message: 'Please complete all required fields' };
    }

    if (
      !dayjs(
        `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}`,
        'YYYY-MM-DD',
        true
      ).isValid()
    ) {
      return {
        isValid: false,
        message: 'Oops. That DOB does not look right. Please try again.',
      };
    }

    return { isValid: true, message: '' };
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const { isValid, message } = validate();
    if (!promptUnsubscribe && !isValid) {
      setSubmitSuccessful(false);
      setSubmitMessage(message);
      return;
    }

    setIsSaving(true);
    setSubmitSuccessful(undefined);

    const unSubscribeDate = calculateUnsubscribeDate();

    //  handle empty string because unsubscribe could result in null field values
    const memberData = {
      given_names: firstName == '' ? null : firstName,
      last_name: lastName == '' ? null : lastName,
      postcode: postcode == '' ? null : postcode,
      gender: gender == '' ? null : gender,
      dob:
        dobDay == ''
          ? null
          : dayjs(
              `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(
                2,
                '0'
              )}`
            ).format('YYYY-MM-DD'),
      email: userInformation.email,
      unsubscribe_date: unSubscribeDate,
      dotDigital: {
        status: !unSubscribeDate ? 'Subscribed' : dotDigital.status,
      },
      subscriptions: promptUnsubscribe
        ? []
        : allSubscriptions
            .filter(subscription => {
              return subscription.isSelected == true;
            })
            .map(({ value }) => value),
    };

    const getProfileData = saveMemberProfile(memberData);

    return Promise.all([getProfileData]).then((profileData) => {
      if (!promptUnsubscribe) {
        //  do not save changes if unsubscribing - may need to change this
        setUserInformation(prevState => {
          return {
            ...prevState,
            ...profileData[0],
          };
        });
      }
    })
    .then(() => {
      refreshMemberSubscriptions();

      if (memberData.unsubscribe_date && promptUnsubscribe) {
        setPromptUnsubscribeSuccessful(true);
      } else {
        setSubmitSuccessful(true);
        setSubmitMessage(block.saveSuccessConfirmationText);
      }

      setPromptUnsubscribe(false);
      setPromptConfirmEmail('');
    })
    .catch(() => {
      setPromptUnsubscribe(false);
      setPromptConfirmEmail('');
      setSubmitSuccessful(false);

      // console.log({error: e.message, email: userInformation.email}, 'saveMemberProfile');
      setSubmitMessage(
        'Oops, there was a problem saving. Please try again later.'
      );
    })
    .finally(() => setIsSaving(false));
  };

  const handleSubscriptionChange = position => {
    const updatedSubscriptions = allSubscriptions.map((item, index) => {
      return index === position
        ? { ...item, isSelected: !item.isSelected }
        : item;
    });
    setAllSubscriptions(updatedSubscriptions);
    setPromptUnsubscribe(false);

    const updatedCheckedState = subscriptionCheckedState.map((item, index) => {
      return index === position ? !item : item;
    });
    setSubscriptionCheckedState(updatedCheckedState);
  };

  return (
    <>
      {isLoading && (
        <Spinner>
          <p>Loading...</p>
        </Spinner>
      )}

      {!isLoading && (
        <>
          {submitSuccessful && !impersonation.cloak && (
            <Pixel firstname={firstName} lastname={lastName} email={memberEmail} />
          )}

          {signOutIsComplete && !impersonation.cloak && (
            <Pixel remove={true} />
          )}

          {!isMember && <MembershipSignupPanel {...block} />}

          {isMember && (
            <>
              <MembershipPanel {...{ ...block, userInformation, isCloaked: impersonation.cloak }} 
                onSignOut={() => {
                  signOut().then(() => {
                    setIsMember(false);
                    setsignOutIsComplete(true);
                  });                  
                }}
              />
              
              <form
                onSubmit={handleProfileSubmit}
                id="profile"
                className={clsx(`needs-validation ${styles.profileContainer}`)}
                noValidate
                autoComplete="off"
              >
                <div className={`membership-form-container ${styles.section}`}>
                  <Spacer width={1160} space={'m'}>
                    <Typography>
                      <h2>{block.profileTitle}</h2>
                      {block.profileDescription && (
                        <p>{block.profileDescription}</p>
                      )}
                      <div
                        className={`formGroupContainer ${styles.formGroupContainer}`}
                      >
                        <div className={`formGroup ${styles.formGroup}`}>
                          <label
                            className="control-label"
                            htmlFor="profile-field-firstname"
                          >
                            First Name <span style={{ color: 'red' }}>*</span>
                          </label>
                          <input
                            className={clsx(`form-control`, {
                              'is-invalid':
                                !firstName &&
                                !submitSuccessful &&
                                !promptUnsubscribeSuccessful,
                            })}
                            id={`profile-field-firstname`}
                            type="text"
                            required
                            value={firstName}
                            autoFocus={submitSuccessful != false}
                            onChange={e => setFirstName(e.target.value)}
                          />
                        </div>
                        <div className={`formGroup ${styles.formGroup}`}>
                          <label
                            className="control-label"
                            htmlFor="profile-field-lastname"
                          >
                            Last Name <span style={{ color: 'red' }}>*</span>
                          </label>
                          <input
                            className={clsx(`form-control`, {
                              'is-invalid':
                                !lastName &&
                                !submitSuccessful &&
                                !promptUnsubscribeSuccessful,
                            })}
                            id={`profile-field-lastname`}
                            type="text"
                            required
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                          />
                        </div>
                        <div
                          className={`formGroup ${styles.formGroup} ${styles.genderGroup}`}
                        >
                          <label
                            className="control-label"
                            htmlFor="profile-field-gender"
                          >
                            Gender <span style={{ color: 'red' }}>*</span>
                          </label>
                          <select
                            id={`profile-field-gender`}
                            className={clsx(`form-control`, {
                              'is-invalid':
                                !gender &&
                                !submitSuccessful &&
                                !promptUnsubscribeSuccessful,
                            })}
                            value={gender}
                            required
                            onChange={e => setGender(e.target.value)}
                          >
                            <option value="">Select your gender</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                            <option value="N/A">Prefer not to say</option>
                          </select>
                        </div>
                        <div
                          className={`formGroup ${styles.formGroup} ${styles.postcodeGroup}`}
                        >
                          <label
                            className="control-label"
                            htmlFor="profile-field-postcode"
                          >
                            Postcode <span style={{ color: 'red' }}>*</span>
                          </label>
                          <input
                            className={clsx(`form-control`, {
                              'is-invalid':
                                !postcode &&
                                !submitSuccessful &&
                                !promptUnsubscribeSuccessful,
                            })}
                            id={`profile-field-postcode`}
                            type="text"
                            required
                            value={postcode}
                            onChange={e => setPostcode(e.target.value)}
                          />
                        </div>
                        <div
                          className={`formGroup ${styles.formGroup} ${styles.dobGroup}`}
                        >
                          <label
                            className="control-label"
                            htmlFor="profile-field-dob"
                          >
                            Date of Birth{' '}
                            <span style={{ color: 'red' }}>*</span>
                          </label>
                          <Spacer
                            row
                            selfH={0}
                            width={`100%`}
                            className={`${styles.dobContainer}`}
                          >
                            <select
                              className={clsx(`form-control pr-2`, {
                                'is-invalid':
                                  !dobDay &&
                                  !submitSuccessful &&
                                  !promptUnsubscribeSuccessful,
                              })}
                              value={dobDay}
                              onChange={e => setDOBDay(e.target.value)}
                            >
                              {Array.from({ length: 31 }, (_, i) => i + 1).map(
                                key => (
                                  <option key={key} value={key}>
                                    {key}
                                  </option>
                                )
                              )}
                            </select>
                            <select
                              className={clsx(`form-control pr-2`, {
                                'is-invalid':
                                  !dobMonth &&
                                  !submitSuccessful &&
                                  !promptUnsubscribeSuccessful,
                              })}
                              value={dobMonth}
                              required
                              onChange={e => setDOBMonth(e.target.value)}
                            >
                              <option value="">Month</option>
                              {months.map((month, key) => (
                                <option key={key} value={month.index}>
                                  {month.full}
                                </option>
                              ))}
                            </select>
                            <select
                              className={clsx(`form-control pr-2`, {
                                'is-invalid':
                                  !dobYear &&
                                  !submitSuccessful &&
                                  !promptUnsubscribeSuccessful,
                              })}
                              value={dobYear}
                              onChange={e => setDOBYear(e.target.value)}
                            >
                              {constructNumberRange(
                                1930,
                                parseInt(
                                  dayjs()
                                    .add(dayjs.duration({ years: -15 }))
                                    .format('YYYY')
                                )
                              )
                                .reverse()
                                .map((year, key) => (
                                  <option key={key} value={year}>
                                    {year}
                                  </option>
                                ))}
                            </select>
                          </Spacer>
                        </div>
                      </div>
                    </Typography>
                    <div
                      className={`membership-form-controls-container justify-content-start
                        ${styles.controlsContainer} ${styles.section}`}
                    >
                      <Spacer
                        row
                        width={1160}
                        space={'m'}
                        align={'center'}
                        justify={'flex-start'}
                      >
                        {isSaving && (
                          <Button disabled={true} onClick={() => {}}>
                            <Icon
                              icon={'autorenew'}
                              rotateIcon={true}
                              className={'mr-2'}
                            />
                            <span>Please wait...</span>
                          </Button>
                        )}
                        {!isSaving && (
                          <Button type={'submit'} 
                            onClick={() => raiseEvent(MEMBERCENTRE_UPDATE_CLICK)}
                          >
                            <span>
                              {dotDigital && dotDigital.status != 'Subscribed'
                                ? block.resubscribeButtonText
                                : block.saveButtonText}
                            </span>
                          </Button>
                        )}
                      </Spacer>
                      <Spacer
                        row
                        width={1160}
                        space={'m'}
                        align={'center'}
                        justify={'center'}
                      >
                        {submitSuccessful === true && (
                          <div
                            className={`alert alert-warning text-center ${styles.alert}`}
                            role="alert"
                          >
                            {submitMessage}
                          </div>
                        )}

                        {submitSuccessful === false && (
                          <div
                            className={`alert alert-danger text-center ${styles.alert}`}
                            role="alert"
                          >
                            {submitMessage}
                          </div>
                        )}
                      </Spacer>
                    </div>
                  </Spacer>
                </div>
                <div
                  id="subscriptions"
                  className={`membership-subscriptions-container ${styles.section} ${styles.noBackground}`}
                >
                  <Spacer width={1160} space={'m'}>
                    <Typography>
                      <h2>{block.subscriptionsTitle}</h2>
                      {block.subscriptionsDescription && (
                        <p>{block.subscriptionsDescription}</p>
                      )}
                      <Spacer
                        row
                        className={`subscriptions ${styles.subscriptions}`}
                      >
                        {allSubscriptions.map(
                          (sub: MemberLevelSubscription, key) => (
                            <label
                              htmlFor={`subscription-checkbox-${key}`}
                              key={key}
                              className={`subscriptionItem ${styles.subscriptionItem}`}
                            >
                              <img src={sub.image} alt={sub.value} />
                              <h4>{sub.label}</h4>
                              <p>{sub.description}</p>
                              <input
                                id={`subscription-checkbox-${key}`}
                                name={sub.value}
                                type="checkbox"
                                value={sub.value}
                                checked={subscriptionCheckedState[key]}
                                onChange={() => handleSubscriptionChange(key)}
                              />
                              <Icon
                                size={'xxxl'}
                                className={
                                  subscriptionCheckedState[key]
                                    ? 'check_circle'
                                    : 'highlight_off'
                                }
                                icon={
                                  subscriptionCheckedState[key]
                                    ? 'check_circle'
                                    : 'highlight_off'
                                }
                              />
                            </label>
                          )
                        )}
                      </Spacer>
                    </Typography>

                    <div
                      className={`membership-form-controls-container ${styles.controlsContainer} ${styles.section} ${styles.noBackground}`}
                    >
                      <Spacer
                        row
                        width={1160}
                        space={['m', 'l']}
                        align={'center'}
                        justify={'center'}
                      >
                        {isSaving && (
                          <>
                            <Button disabled={true} onClick={() => {}}>
                              <Icon
                                icon={'autorenew'}
                                rotateIcon={true}
                                className={'mr-2'}
                              />
                              <span>Please wait...</span>
                            </Button>
                            <Button disabled={true} onClick={() => {}}>
                              <Icon
                                icon={'autorenew'}
                                rotateIcon={true}
                                className={'mr-2'}
                              />
                              <span>Please wait...</span>
                            </Button>
                          </>
                        )}
                        {!isSaving && (
                          <>
                            <Button
                              type={'submit'}
                              onClick={() => {
                                setSubmitSuccessful(undefined);
                                setPromptUnsubscribeSuccessful(undefined);
                                setPromptUnsubscribe(false);
                                setPromptConfirmEmail('');
                                setUnsubscribeDate(Date.now());
                                raiseEvent(MEMBERCENTRE_UPDATE_CLICK);
                                // console.log('Save Membership');
                              }}
                            >
                              <span>
                                {dotDigital && dotDigital.status != 'Subscribed'
                                  ? block.resubscribeButtonText
                                  : block.saveButtonText}
                              </span>
                            </Button>
                            {dotDigital && dotDigital.status == 'Subscribed' && (
                              <Button
                                type="button"
                                onClick={() => {
                                  setPromptUnsubscribe(true);
                                  setPromptUnsubscribeSuccessful(undefined);
                                  setSubmitSuccessful(undefined);
                                  // console.log('Unsubscribe All Prompt');
                                }}
                              >
                                <span>{block.unsubscribeButtonText}</span>
                              </Button>
                            )}
                          </>
                        )}
                      </Spacer>
                      <Spacer
                        row
                        width={1160}
                        space={'m'}
                        align={'center'}
                        justify={'center'}
                      >
                        {submitSuccessful === true && (
                          <div
                            className={`alert alert-warning text-center ${styles.alert}`}
                            role="alert"
                          >
                            {submitMessage}
                          </div>
                        )}

                        {submitSuccessful === false && (
                          <div
                            className={`alert alert-danger text-center ${styles.alert}`}
                            role="alert"
                          >
                            {submitMessage}
                          </div>
                        )}

                        {promptUnsubscribe && (
                          <div
                            className={`alert alert-warning ${styles.alert}`}
                            role="alert"
                          >
                            <Spacer
                              row={false}
                              space={'m'}
                              align={'center'}
                              justify={'center'}
                            >
                              <p>{block.unsubscribePromptText}</p>

                              <div className="mt-0">
                                <label
                                  className="control-label text-left px-0"
                                  htmlFor="unsubscribe-email-confirm"
                                >
                                  Confirm your email address and proceed{' '}
                                  <span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                  className={`form-control`}
                                  id={`unsubscribe-email-confirm`}
                                  type="email"
                                  required
                                  autoFocus={promptUnsubscribe}
                                  value={promptConfirmEmail}
                                  onChange={e =>
                                    setPromptConfirmEmail(e.target.value)
                                  }
                                />
                              </div>

                              <Spacer
                                row
                                space={'m'}
                                align={'center'}
                                justify={'center'}
                                className={`mt-2`}
                              >
                                <Button
                                  type={'submit'}
                                  disabled={promptConfirmEmail !== memberEmail}
                                  onClick={() => {
                                    setUnsubscribeDate(Date.now());
                                    raiseEvent(MEMBERCENTRE_UNSUBSCRIBE_CLICK);
                                    // console.log('Do Unsubscribe All');
                                  }}
                                >
                                  <span>{block.unsubscribeButtonText}</span>
                                </Button>
                                <Button
                                  className={`ml-2`}
                                  type="button"
                                  onClick={() => {
                                    setPromptUnsubscribe(false);
                                    setPromptUnsubscribeSuccessful(undefined);
                                    setPromptConfirmEmail('');
                                    // console.log('Cancel Unsubscribe Prompt');
                                  }}
                                >
                                  <span>Cancel</span>
                                </Button>
                              </Spacer>
                            </Spacer>
                          </div>
                        )}

                        {promptUnsubscribeSuccessful && (
                          <div
                            className={`alert alert-warning text-center ${styles.alert}`}
                            role="alert"
                          >
                            <p>{block.unsubscribeConfirmationText}</p>
                          </div>
                        )}
                      </Spacer>
                    </div>
                  </Spacer>
                </div>
              </form>
            </>
          )}
        </>
      )}
    </>
  );
};

export default compose(styled(membershipBlockStyles))(MembershipBlock);
